// Basic
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Store & actions
import useMerchantAccountStore from '@/app/store/modules/merchantAccount';
import useSessionStore from '@/app/store/session';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import Entity from '@/common-utils/components/renders/entity/Entity';
import MerchantUserTable from '@/app/modules/users/merchant/components/tables/MerchantUsersTable';
import ModalAddContracts from '@/app/modules/accounts/components/modals/addContracts/ModalAddContracts';
import ModalAddExternalContract from '@/app/modules/accounts/components/modals/addExternalContract/ModalAddExternalContract';
import ModalAddMerchantUser from '@/app/modules/users/merchant/components/modals/addMerchantUser/ModalAddMerchantUser';
import ModalDeleteMultipleContracts from '@/app/modules/accounts/components/modals/deleteMultipleContracts/ModalDeleteMultipleContracts';
import ContractsTable from '@/app/modules/contracts/components/tables/ContractsTable';
import CustomizationTable from '@/app/modules/payludd/customization/components/tables/CustomizationTable';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import Icon from '@/common-utils/components/renders/icon/Icon';
import PayluddSubscriptionsTable from '@/app/modules/payludd/subscriptions/components/tables/MerchantAccountPayluddSubscriptionsTable';
import PayluddBeneficiaries from '@/app/modules/payludd/beneficiaries/components/PayluddBeneficiaries';
// Common
import RouteUrls from '../../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import Permissions from '@/app/permissions/Permissions';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import { buildUrl, extractQueriesFromUrl, replaceParameters } from '@/common-utils/functions/restUtils';
import useTabManager from '../../hooks/useTabManager';
import MERCHANT_ACCOUNT_DETAILS_TABS from '../../enums/tabs/MerchantAccountDetailsTabs';
// Lang
import { nsContract, nsCustomization, nsMerchantUser, nsPayluddSubscription } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @returns {JSX} MerchantAccountDetails
 */
function MerchantAccountDetails() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { contractAccessId: merchantAccountId } = useParams();
    const { key, view } = extractQueriesFromUrl(location.search);

    // Store
    const { permissions } = useSessionStore(s => s.session);
    const merchantAccountDetails = useMerchantAccountStore(s => s.merchantAccountDetails);

    // State
    // Add Merchant User Modal
    const [showModalAddMerchantUser, setShowModalAddMerchantUser] = useState(false);
    // Add Contracts Modal
    const [showModalAddContracts, setShowModalAddContracts] = useState(false);
    // Add External Contracts Modal
    const [showModalAddExternalContract, setShowModalAddExternalContract] = useState(false);
    // Add External Contracts Modal
    const [showModalDeleteMultipleContracts, setShowModalDeleteMultipleContracts] = useState(false);
    const [activeTab, setActiveTab] = useState(Object.values(MERCHANT_ACCOUNT_DETAILS_TABS)
        .find(tab => tab.name === view)?.name ?? MERCHANT_ACCOUNT_DETAILS_TABS.DEFAULT.name);
    const [isLoading, setIsLoading] = useState(true);

    // Hooks
    useEffect(() => {
        if (key) {
            setShowModalAddExternalContract(true);
        }
    }, [key]);

    // Handles tab selection
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: MERCHANT_ACCOUNT_DETAILS_TABS,
        navigate: navigate
    });

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.MERCHANT_ACCOUNT_DETAILS}
                    i18nData={{ label: merchantAccountDetails.label }}
                    isLoading={isLoading}
                    routeBase={RouteUrls.MERCHANT_ACCOUNTS}
                >
                    <TabsList>
                        <TabsTrigger tab={MERCHANT_ACCOUNT_DETAILS_TABS.CONTRACTS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.merchant_accounts.details.tabs.merchant_contracts' />
                        </TabsTrigger>
                        <TabsTrigger tab={MERCHANT_ACCOUNT_DETAILS_TABS.USERS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.merchant_accounts.details.tabs.merchant_users' />
                        </TabsTrigger>
                        <TabsTrigger tab={MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.merchant_accounts.details.tabs.customization' />
                        </TabsTrigger>
                        <TooltipHelper
                            asChild
                            trigger={
                                <TabsTrigger tab={MERCHANT_ACCOUNT_DETAILS_TABS.SUBSCRIPTIONS} activeTab={activeTab} permissions={permissions} >
                                    <Entity entity='page.merchant_accounts.details.tabs.subscriptions' />
                                    <Icon icon={ICONS.HELP} className='ml-1' />
                                </TabsTrigger>
                            }
                            content={<Entity entity='tooltip.subscriptions_tab' namespace={nsPayluddSubscription} />}
                        />
                        <TabsTrigger tab={MERCHANT_ACCOUNT_DETAILS_TABS.BENEFICIARIES} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.merchant_accounts.details.tabs.beneficiaries' />
                        </TabsTrigger>
                    </TabsList>
                </Page.Header>
                <Page.Body data-cy='page-contract-access'>
                    <TabsContent tab={MERCHANT_ACCOUNT_DETAILS_TABS.CONTRACTS} permissions={permissions}>
                        <ContractsTable
                            filtersEventName={setFiltersEventName('contracts')}
                            merchantAccountId={merchantAccountId}
                            setIsLoading={setIsLoading}
                            pageActions={
                                <PageActions
                                    actions={[
                                        {
                                            isDropdown: true,
                                            visible: true,
                                            trigger: {
                                                icon: ICONS.ADD,
                                                entity: 'actions.page.add_contracts.title',
                                                namespace: nsContract,
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.CONTRACTS_ACCESS_CREATE_CONTRACT_PROFILE.name,
                                                        Permissions.MERCHANT_CONTRACT_LIST_FROM_DIRECT_PROVIDER.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            subActions: [
                                                {
                                                    entity: 'actions.page.add_contracts.gateway',
                                                    namespace: nsContract,
                                                    onClick: () => setShowModalAddContracts(true),
                                                    visible: true,
                                                    disabled: !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [
                                                            Permissions.CONTRACTS_ACCESS_CREATE_CONTRACT_PROFILE.name,
                                                            Permissions.MERCHANT_CONTRACT_LIST_FROM_DIRECT_PROVIDER.name
                                                        ]
                                                    }),
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                },
                                                {
                                                    entity: 'actions.page.add_contracts.external',
                                                    namespace: nsContract,
                                                    onClick: () => setShowModalAddExternalContract(true),
                                                    visible: true,
                                                    disabled: !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [
                                                            Permissions.ALMA_ENTER_API_KEY.name
                                                        ]
                                                    }),
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                }
                                            ]
                                        },
                                        {
                                            icon: ICONS.CLOSE,
                                            entity: 'actions.page.delete_multiple_contracts',
                                            namespace: nsMerchantUser,
                                            variant: 'critical',
                                            onClick: () => setShowModalDeleteMultipleContracts(true),
                                            visible: true,
                                            disabled: !hasRequiredPermissions({
                                                permissions: permissions,
                                                requiredPermissions: [Permissions.CONTRACTS_ACCESS_DELETE_CONTRACT_PROFILE.name]
                                            }),
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        }
                                    ]}
                                    retractable
                                />
                            } />

                    </TabsContent>
                    <TabsContent tab={MERCHANT_ACCOUNT_DETAILS_TABS.USERS} permissions={permissions}>
                        <MerchantUserTable
                            filtersEventName={setFiltersEventName('merchant_users')}
                            pageActions={
                                <PageActions
                                    actions={[{
                                        icon: ICONS.USER_ADD,
                                        entity: 'actions.page.add_merchant_user',
                                        namespace: nsMerchantUser,
                                        onClick: () => setShowModalAddMerchantUser(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [Permissions.CONTRACTS_ACCESS_CREATE_USER.name]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    }]}
                                    retractable
                                />
                            } />
                    </TabsContent>
                    <TabsContent tab={MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION} permissions={permissions}>
                        <CustomizationTable
                            filtersEventName={setFiltersEventName('customization')}
                            pageActions={
                                <PageActions
                                    actions={[{
                                        icon: ICONS.ADD,
                                        entity: 'actions.page.add_customization',
                                        namespace: nsCustomization,
                                        onClick: () => {
                                            const newCustomization = buildUrl(
                                                replaceParameters(RouteUrls.PAYLUDD_CUSTOMIZATION_CREATION.url, { contractAccessId: merchantAccountId }),
                                                { view: MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION.name }
                                            );
                                            navigate(newCustomization);
                                        },
                                        visible: true
                                    }]}
                                    retractable
                                />
                            } />

                    </TabsContent>
                    <TabsContent tab={MERCHANT_ACCOUNT_DETAILS_TABS.SUBSCRIPTIONS} permissions={permissions}>
                        <PayluddSubscriptionsTable
                            filtersEventName={setFiltersEventName('payludd_subscriptions')}
                            merchantAccountId={merchantAccountId}
                        />
                    </TabsContent>
                    <TabsContent tab={MERCHANT_ACCOUNT_DETAILS_TABS.BENEFICIARIES} permissions={permissions}>
                        <PayluddBeneficiaries merchantAccountId={merchantAccountId} />
                    </TabsContent>
                </Page.Body>
            </Tabs>
            {
                showModalAddContracts &&
                <ModalAddContracts
                    isOpen={showModalAddContracts}
                    setIsOpen={setShowModalAddContracts}
                    merchantAccountId={merchantAccountId}
                    merchantAccountDetails={merchantAccountDetails}
                />
            }
            {
                showModalAddExternalContract &&
                (<ModalAddExternalContract
                    isOpen={showModalAddExternalContract}
                    setIsOpen={setShowModalAddExternalContract}
                    merchantAccountDetails={merchantAccountDetails}
                />)
            }
            {
                showModalAddMerchantUser &&
                <ModalAddMerchantUser
                    isOpen={showModalAddMerchantUser}
                    setIsOpen={setShowModalAddMerchantUser}
                    merchantAccountId={merchantAccountId}
                />
            }
            {
                showModalDeleteMultipleContracts &&
                <ModalDeleteMultipleContracts
                    isOpen={showModalDeleteMultipleContracts}
                    setIsOpen={setShowModalDeleteMultipleContracts}
                    merchantAccountDetails={merchantAccountDetails}
                />
            }
        </Page>
    );
}

export default MerchantAccountDetails;
